import React from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { RiskStatistics } from "@sscs/report/ui";
import { convertFinalCheckLabel } from "@sscs/report/utils";
import {
  Card,
  CheckLabel,
  DotSeparator,
  LongTextLabel,
  Menu,
  Section,
  Select,
  ValueOrPlaceholder,
} from "@sscs/ui";
import { getReleaseDateDisplay } from "@sscs/utils";
import { ApprovalStatus, FileTypeIcon, LinkToReport } from "Components";
import { SelectedVersionType, Version } from "Types";
import { useVersionReportSummary } from "../../../hooks";
import styles from "./latestVersion.module.scss";

const VERSION_TYPE_LABEL: Record<SelectedVersionType, string> = {
  approved: "Latest approved version",
  uploaded: "Latest uploaded version",
  released: "Latest released version",
};

const NO_VERSION_TEXT: Record<SelectedVersionType, string> = {
  approved:
    "No approved versions. To see a summary for the latest approved version, mark one as approved from the action menu.",
  released:
    "No released versions. To see a summary for the latest released version, mark one as released from the action menu.",
  uploaded: "There are no versions in this package.",
};

type LatestVersionProps = {
  selectedVersionType: SelectedVersionType;
  version?: Version;
  onSelectedVersionChange: (selectedVersionType: SelectedVersionType) => void;
};

const LatestVersion = ({
  selectedVersionType,
  version,
  onSelectedVersionChange,
}: LatestVersionProps): React.JSX.Element => {
  const { reportSummary } = useVersionReportSummary(version);
  const { status, scanLevel, isRepro, isDiff } = convertFinalCheckLabel(
    reportSummary?.scan_report?.checks,
  );

  return (
    <Card.Card>
      <Card.Title
        as="h2"
        actions={
          <Select
            id="select-representative-version"
            labelId="select-representative-version-label"
            label="Show summary for"
            shrinkLabel
            value={selectedVersionType}
            onChange={(value) =>
              onSelectedVersionChange(value as SelectedVersionType)
            }
          >
            <Menu.Item value="approved">
              {VERSION_TYPE_LABEL.approved}
            </Menu.Item>
            <Menu.Item value="uploaded">
              {VERSION_TYPE_LABEL.uploaded}
            </Menu.Item>
            <Menu.Item value="released">
              {VERSION_TYPE_LABEL.released}
            </Menu.Item>
          </Select>
        }
      >
        {`${VERSION_TYPE_LABEL[selectedVersionType]} summary`}
      </Card.Title>
      <Card.Content className={styles.content}>
        {version ? (
          <>
            <div className={styles.identity}>
              <FileTypeIcon
                fileName={version.file_name}
                architecture={version.software_info.architecture}
              />
              <div className={styles.details}>
                <div className={styles.headline}>
                  <div className={styles.version}>
                    {version.software_info.version ? (
                      <LongTextLabel label={version.software_info.version} />
                    ) : (
                      <ValueOrPlaceholder value={undefined} />
                    )}
                  </div>
                  <ApprovalStatus
                    status={version.approval_status}
                    info={version.approval_information}
                    variant="text"
                  />
                </div>
                <div className={styles.metadata}>
                  <span>
                    <span className={styles.label}>Publisher:</span>{" "}
                    <strong>
                      <ValueOrPlaceholder
                        value={version.software_info.publisher}
                      />
                    </strong>
                  </span>
                  <DotSeparator />
                  <span>
                    <span className={styles.label}>License:</span>{" "}
                    <strong>
                      <ValueOrPlaceholder
                        value={version.software_info.license}
                      />
                    </strong>
                  </span>
                  <DotSeparator />
                  <span>
                    <span className={styles.label}>Platform:</span>{" "}
                    <strong>
                      <ValueOrPlaceholder
                        value={version.software_info.platform}
                      />
                    </strong>
                  </span>
                  <DotSeparator />
                  <span>
                    <span className={styles.label}>Architecture:</span>{" "}
                    <strong>
                      <ValueOrPlaceholder
                        value={version.software_info.architecture}
                      />
                    </strong>
                  </span>
                  <DotSeparator />
                  <span>
                    <span className={styles.label}>Category:</span>{" "}
                    <strong>
                      <ValueOrPlaceholder
                        value={version.software_info.category}
                      />
                    </strong>
                  </span>
                  <DotSeparator />
                  <span>
                    <span className={styles.label}>Release date:</span>{" "}
                    <strong>
                      <ValueOrPlaceholder
                        value={getReleaseDateDisplay(
                          version.software_info.release_date,
                        )}
                      />
                    </strong>
                  </span>
                </div>
              </div>
            </div>
            {reportSummary?.scan_report && (
              <Section>
                <Section.Title as="h3">SAFE assessment</Section.Title>
                <Section.Action>
                  <span
                    className={styles.lastScan}
                    title={
                      version.artifacts_summary.version.current_status.timestamp
                    }
                  >
                    Last scan:{" "}
                    {formatDistanceToNowStrict(
                      new Date(
                        version.artifacts_summary.version.current_status.timestamp,
                      ),
                    )}{" "}
                    ago
                  </span>
                </Section.Action>
                <Section.Content className={styles.assessment}>
                  <CheckLabel
                    status={status}
                    scanLevel={scanLevel}
                    isLevelCustomized={
                      reportSummary.scan_report.inhibitors.customized
                    }
                    isReproducibilityCheck={isRepro}
                    isDiffCheck={isDiff}
                  />
                  <RiskStatistics
                    risk={reportSummary.scan_report.assessments}
                    riskCountWrapper={(assessment, category, children) => (
                      <LinkToReport
                        id={`last-released-version-${category}-link`}
                        report={{
                          projectName: version.project_name,
                          packageName: version.package_name,
                          version: version.software_info.version,
                          isRepro: isRepro,
                          riskCategory: category,
                          riskViolations: assessment.violations,
                        }}
                      >
                        {children}
                      </LinkToReport>
                    )}
                    viewReportWrapper={(children) => (
                      <LinkToReport
                        id={`view-last-released-version-report`}
                        report={{
                          projectName: version.project_name,
                          packageName: version.package_name,
                          version: version.software_info.version,
                          isRepro: isRepro,
                        }}
                      >
                        {children}
                      </LinkToReport>
                    )}
                  />
                </Section.Content>
              </Section>
            )}
          </>
        ) : (
          NO_VERSION_TEXT[selectedVersionType]
        )}
      </Card.Content>
    </Card.Card>
  );
};

export default LatestVersion;
